/**
 * Binary search for the last item whose key is ≤ target.
 *
 * Returns the item, or null if no such item exists (all keys > target, or
 * empty input). Time: O(log n). Caller is responsible for `items` being
 * sorted by `getKey` ascending.
 *
 * Used by:
 *   - LogOverlay.vm / LogsTab.vm — active log at scrub timestamp
 *   - TimelineStrip.vm — active segment, active mission-state run, current
 *     speed sample
 *   - TimelineTab.vm — mobile equivalents of the three above
 *
 * Callers needing an additional upper-bound check (e.g. "the run that
 * *contains* `target`, not just the last one starting at-or-before") should
 * compose this primitive with their own end-check, since `end` semantics are
 * caller-specific.
 */
export function findLastAtOrBefore<T>(items: readonly T[], target: number, getKey: (item: T) => number): T | null {
	const idx = findLastIndexAtOrBefore(items, target, getKey);
	return idx < 0 ? null : items[idx];
}

/**
 * Index variant of {@link findLastAtOrBefore}. Returns -1 if no item has
 * key ≤ target.
 */
export function findLastIndexAtOrBefore<T>(items: readonly T[], target: number, getKey: (item: T) => number): number {
	if (items.length === 0) return -1;
	let lo = 0;
	let hi = items.length - 1;
	while (lo < hi) {
		const mid = (lo + hi + 1) >>> 1;
		if (getKey(items[mid]) <= target) lo = mid;
		else hi = mid - 1;
	}
	return getKey(items[lo]) <= target ? lo : -1;
}
